import Button from '@orn/shared-web/components/Button/Button';
import * as Sentry from '@sentry/react';
import * as React from 'react';

import { IS_PROD } from '../../env';

interface IErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface IErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  constructor(props: IErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // just send it to Sentry, no need to log it to the console (it is shown on the screen)
    // by calling sentry directly we can send the component stack to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
          digest: (errorInfo as React.ErrorInfo & { digest?: string }).digest,
        },
      },
      tags: {
        errorBoundary: true,
      },
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8">
          <div>
            {IS_PROD ? (
              <div className="text-xl font-semibold">Something went wrong</div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="text-xl font-semibold">
                  {this.state.error?.message || 'An unexpected error occurred'}
                </div>
                <pre className="text-tertiary wrap-break-word max-w-full whitespace-pre-wrap text-left">
                  {this.state.error?.stack}
                </pre>
              </div>
            )}
          </div>
          <Button onClick={this.handleReset} color="primary" size="md">
            Reload
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
